import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Plus, Trash2, Save, Building2, Users, FileText, Tag, Home, Briefcase, MapPin, Image, Upload, X, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default configuration
const DEFAULT_CONFIG = {
  property_types: [
    { value: "residential", label: "Residential", icon: "Home" },
    { value: "commercial", label: "Commercial", icon: "Briefcase" },
    { value: "land", label: "Land", icon: "MapPin" }
  ],
  property_subtypes: {
    residential: [
      { value: "apartment", label: "Apartment/Flat" },
      { value: "independent_house", label: "Independent House" },
      { value: "villa", label: "Villa" },
      { value: "builder_floor", label: "Builder Floor" },
      { value: "penthouse", label: "Penthouse" },
      { value: "studio", label: "Studio Apartment" },
      { value: "farmhouse", label: "Farm House" },
      { value: "row_house", label: "Row House" }
    ],
    commercial: [
      { value: "office_space", label: "Office Space" },
      { value: "shop", label: "Shop" },
      { value: "showroom", label: "Showroom" },
      { value: "warehouse", label: "Warehouse/Godown" },
      { value: "industrial_shed", label: "Industrial Shed" },
      { value: "co_working", label: "Co-working Space" },
      { value: "business_center", label: "Business Center" },
      { value: "it_park", label: "IT Park/SEZ" },
      { value: "hotel", label: "Hotel/Resort" },
      { value: "restaurant", label: "Restaurant/Cafe" }
    ],
    land: [
      { value: "residential_plot", label: "Residential Plot" },
      { value: "commercial_plot", label: "Commercial Plot" },
      { value: "industrial_plot", label: "Industrial Plot" },
      { value: "agricultural_land", label: "Agricultural Land" },
      { value: "farm_land", label: "Farm Land" },
      { value: "na_plot", label: "NA Plot" },
      { value: "institutional_land", label: "Institutional Land" }
    ]
  },
  lead_sources: [
    { value: "website", label: "Website" },
    { value: "website_inquiry", label: "Website Inquiry" },
    { value: "referral", label: "Referral" },
    { value: "walk_in", label: "Walk-in" },
    { value: "phone", label: "Phone Call" },
    { value: "social_media", label: "Social Media" },
    { value: "property_portal", label: "Property Portal" },
    { value: "newspaper", label: "Newspaper Ad" },
    { value: "hoarding", label: "Hoarding/Billboard" },
    { value: "other", label: "Other" }
  ],
  lead_statuses: [
    { value: "new", label: "New", color: "blue" },
    { value: "contacted", label: "Contacted", color: "yellow" },
    { value: "qualified", label: "Qualified", color: "purple" },
    { value: "negotiation", label: "Negotiation", color: "orange" },
    { value: "converted", label: "Converted", color: "green" },
    { value: "lost", label: "Lost", color: "red" }
  ],
  document_types: [
    { value: "title_deed", label: "Title Deed" },
    { value: "agreement", label: "Sale Agreement" },
    { value: "noc", label: "NOC" },
    { value: "possession_letter", label: "Possession Letter" },
    { value: "tax_receipt", label: "Tax Receipt" },
    { value: "id_proof", label: "ID Proof" },
    { value: "income_proof", label: "Income Proof" },
    { value: "bank_statement", label: "Bank Statement" },
    { value: "rera_certificate", label: "RERA Certificate" },
    { value: "completion_certificate", label: "Completion Certificate" },
    { value: "other", label: "Other Document" }
  ],
  amenities: [
    "Swimming Pool", "Gym/Fitness Center", "Clubhouse", "Children's Play Area",
    "Garden/Landscaping", "24x7 Security", "CCTV Surveillance", "Power Backup",
    "Lift/Elevator", "Covered Parking", "Visitor Parking", "Intercom",
    "Fire Safety", "Rain Water Harvesting", "Sewage Treatment Plant",
    "Jogging Track", "Tennis Court", "Basketball Court", "Badminton Court",
    "Indoor Games", "Library", "Party Hall", "Guest Rooms", "ATM",
    "Convenience Store", "Cafeteria", "Amphitheatre", "Temple/Prayer Hall"
  ],
  property_features: [
    "Vastu Compliant", "Corner Property", "Road Facing", "Park Facing",
    "Gated Community", "Premium Location", "Near Metro", "Near School",
    "Near Hospital", "Near Mall", "Near Highway", "Pet Friendly",
    "Senior Citizen Friendly", "Wheelchair Accessible", "Smart Home Features",
    "Solar Panels", "EV Charging Point", "Terrace Garden", "Private Pool",
    "Home Theatre", "Servant Quarter", "Study Room", "Pooja Room"
  ]
};

export default function AdminSettings() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("property_types");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addDialogType, setAddDialogType] = useState("");
  const [newItem, setNewItem] = useState({ value: "", label: "" });
  const [selectedParent, setSelectedParent] = useState("");
  
  // Site Settings state
  const [siteSettings, setSiteSettings] = useState({ home_page_image: null });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  
  // Contact Settings state
  const [contactSettings, setContactSettings] = useState({
    admin_phone: "+91 96991 36479",
    admin_whatsapp: "919699136479",
    admin_email: "contact@capinrealtypune.com",
    support_hours: "Mon-Sat, 9:00 AM - 6:00 PM IST"
  });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/site-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSiteSettings(response.data || { home_page_image: null });
      // Set contact settings from response
      if (response.data) {
        setContactSettings({
          admin_phone: response.data.admin_phone || "+91 96991 36479",
          admin_whatsapp: response.data.admin_whatsapp || "919699136479",
          admin_email: response.data.admin_email || "contact@capinrealtypune.com",
          support_hours: response.data.support_hours || "Mon-Sat, 9:00 AM - 6:00 PM IST"
        });
      }
    } catch (error) {
      console.log("Could not fetch site settings");
    }
  };

  const handleSaveContactSettings = async () => {
    setSavingContact(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/site-settings/contact`, contactSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Contact settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contact settings");
    } finally {
      setSavingContact(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }
    
    setUploadingImage(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("image", file);
      
      const response = await axios.post(`${API}/site-settings/home-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      setSiteSettings(prev => ({ ...prev, home_page_image: response.data.image_url }));
      toast.success("Home page image updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    setDeletingImage(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/site-settings/home-image`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSiteSettings(prev => ({ ...prev, home_page_image: null }));
      toast.success("Home page image removed");
    } catch (error) {
      toast.error("Failed to remove image");
    } finally {
      setDeletingImage(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setConfig({ ...DEFAULT_CONFIG, ...response.data });
      }
    } catch (error) {
      // Use default config if not found
      console.log("Using default configuration");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/config`, config, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const openAddDialog = (type, parent = "") => {
    setAddDialogType(type);
    setSelectedParent(parent);
    setNewItem({ value: "", label: "", color: "blue" });
    setShowAddDialog(true);
  };

  const handleAddItem = () => {
    if (!newItem.value || !newItem.label) {
      toast.error("Please fill in all fields");
      return;
    }

    const itemToAdd = {
      value: newItem.value.toLowerCase().replace(/\s+/g, "_"),
      label: newItem.label,
      ...(newItem.color && { color: newItem.color })
    };

    setConfig(prev => {
      const updated = { ...prev };
      
      if (addDialogType === "property_subtypes" && selectedParent) {
        updated.property_subtypes = {
          ...updated.property_subtypes,
          [selectedParent]: [...(updated.property_subtypes[selectedParent] || []), itemToAdd]
        };
      } else if (addDialogType === "amenities" || addDialogType === "property_features") {
        updated[addDialogType] = [...updated[addDialogType], newItem.label];
      } else {
        updated[addDialogType] = [...updated[addDialogType], itemToAdd];
      }
      
      return updated;
    });

    setShowAddDialog(false);
    toast.success("Item added successfully");
  };

  const handleRemoveItem = (type, index, parent = "") => {
    setConfig(prev => {
      const updated = { ...prev };
      
      if (type === "property_subtypes" && parent) {
        updated.property_subtypes = {
          ...updated.property_subtypes,
          [parent]: updated.property_subtypes[parent].filter((_, i) => i !== index)
        };
      } else {
        updated[type] = updated[type].filter((_, i) => i !== index);
      }
      
      return updated;
    });
    toast.success("Item removed");
  };

  const tabs = [
    { id: "property_types", label: "Property Types", icon: Building2 },
    { id: "lead_sources", label: "Lead Sources", icon: Users },
    { id: "lead_statuses", label: "Lead Statuses", icon: Tag },
    { id: "document_types", label: "Document Types", icon: FileText },
    { id: "amenities", label: "Amenities", icon: Home },
    { id: "property_features", label: "Features", icon: Tag },
    { id: "site_settings", label: "Site Settings", icon: Image },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-semibold text-[#1e3a5f] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Admin Settings
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Customize property types, lead sources, and other application options
              </p>
            </div>
            <Button
              onClick={saveConfig}
              disabled={saving}
              className="bg-gradient-to-r from-[#d4af37] to-[#b08d26] text-[#1e3a5f] font-semibold hover:shadow-lg transition-all rounded-lg"
              data-testid="save-settings-btn"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? "Saving..." : "Save All Changes"}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "text-[#1e3a5f] border-b-3 border-[#d4af37] bg-[#f0f4f8]"
                    : "text-slate-600 hover:text-[#1e3a5f] hover:bg-slate-50"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Property Types Tab */}
            {activeTab === "property_types" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Property Types & Subtypes</h3>
                </div>

                {config.property_types.map((type) => (
                  <div key={type.value} className="border border-slate-200 rounded-xl p-6 bg-slate-50">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-[#1e3a5f] flex items-center justify-center">
                          {type.icon === "Home" && <Home className="h-5 w-5 text-white" />}
                          {type.icon === "Briefcase" && <Briefcase className="h-5 w-5 text-white" />}
                          {type.icon === "MapPin" && <MapPin className="h-5 w-5 text-white" />}
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#1e3a5f]">{type.label}</h4>
                          <p className="text-xs text-slate-500">Code: {type.value}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => openAddDialog("property_subtypes", type.value)}
                        size="sm"
                        variant="outline"
                        className="rounded-lg"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Subtype
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {(config.property_subtypes[type.value] || []).map((subtype, index) => (
                        <div
                          key={subtype.value}
                          className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-slate-200 group"
                        >
                          <span className="text-sm text-slate-700">{subtype.label}</span>
                          <button
                            onClick={() => handleRemoveItem("property_subtypes", index, type.value)}
                            className="opacity-0 group-hover:opacity-100 text-[#0f2744] hover:text-[#1e3a5f] transition-opacity"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Lead Sources Tab */}
            {activeTab === "lead_sources" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Lead Sources</h3>
                  <Button
                    onClick={() => openAddDialog("lead_sources")}
                    size="sm"
                    className="bg-[#1e3a5f] text-white rounded-lg"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Source
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {config.lead_sources.map((source, index) => (
                    <div
                      key={source.value}
                      className="flex items-center justify-between bg-slate-50 px-4 py-3 rounded-lg border border-slate-200 group"
                    >
                      <span className="text-sm font-medium text-slate-700">{source.label}</span>
                      <button
                        onClick={() => handleRemoveItem("lead_sources", index)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lead Statuses Tab */}
            {activeTab === "lead_statuses" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Lead Statuses</h3>
                  <Button
                    onClick={() => openAddDialog("lead_statuses")}
                    size="sm"
                    className="bg-[#1e3a5f] text-white rounded-lg"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Status
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {config.lead_statuses.map((status, index) => (
                    <div
                      key={status.value}
                      className="flex items-center justify-between bg-slate-50 px-4 py-3 rounded-lg border border-slate-200 group"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`status-badge status-${status.value}`}>{status.label}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveItem("lead_statuses", index)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Document Types Tab */}
            {activeTab === "document_types" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Document Types</h3>
                  <Button
                    onClick={() => openAddDialog("document_types")}
                    size="sm"
                    className="bg-[#1e3a5f] text-white rounded-lg"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Type
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {config.document_types.map((docType, index) => (
                    <div
                      key={docType.value}
                      className="flex items-center justify-between bg-slate-50 px-4 py-3 rounded-lg border border-slate-200 group"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[#1e3a5f]" />
                        <span className="text-sm font-medium text-slate-700">{docType.label}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveItem("document_types", index)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities Tab */}
            {activeTab === "amenities" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Property Amenities</h3>
                  <Button
                    onClick={() => openAddDialog("amenities")}
                    size="sm"
                    className="bg-[#1e3a5f] text-white rounded-lg"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Amenity
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {config.amenities.map((amenity, index) => (
                    <div
                      key={amenity}
                      className="flex items-center gap-2 bg-[#f0f4f8] px-3 py-2 rounded-full border border-[#d9e2ec] group"
                    >
                      <span className="text-sm text-[#1e3a5f]">{amenity}</span>
                      <button
                        onClick={() => handleRemoveItem("amenities", index)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Property Features Tab */}
            {activeTab === "property_features" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Property Features</h3>
                  <Button
                    onClick={() => openAddDialog("property_features")}
                    size="sm"
                    className="bg-[#1e3a5f] text-white rounded-lg"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Feature
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {config.property_features.map((feature, index) => (
                    <div
                      key={feature}
                      className="flex items-center gap-2 bg-[#fffdf5] px-3 py-2 rounded-full border border-[#d4af37]/30 group"
                    >
                      <span className="text-sm text-[#1e3a5f]">{feature}</span>
                      <button
                        onClick={() => handleRemoveItem("property_features", index)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Site Settings Tab */}
            {activeTab === "site_settings" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">Home Page Background Image</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Upload a custom background image for the login page. Recommended size: 1920x1080 pixels.
                  </p>
                </div>

                {/* Current Image Preview */}
                {siteSettings.home_page_image ? (
                  <div className="space-y-4">
                    <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                      <img 
                        src={siteSettings.home_page_image} 
                        alt="Home page background" 
                        className="w-full h-64 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 text-white">
                        <p className="font-medium">Current Home Page Image</p>
                        <p className="text-sm text-white/80">Click below to change or remove</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full rounded-lg border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f]/5"
                          disabled={uploadingImage}
                          onClick={() => document.querySelector('input[type="file"]').click()}
                          data-testid="change-home-image-btn"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingImage ? "Uploading..." : "Change Image"}
                        </Button>
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-lg border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744]/10"
                        onClick={handleDeleteImage}
                        disabled={deletingImage}
                        data-testid="remove-home-image-btn"
                      >
                        <X className="h-4 w-4 mr-2" />
                        {deletingImage ? "Removing..." : "Remove"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="h-16 w-16 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                        <Image className="h-8 w-8 text-[#1e3a5f]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1e3a5f]">No custom image set</p>
                        <p className="text-sm text-slate-500 mt-1">
                          The login page will use the default blue gradient background
                        </p>
                      </div>
                      <label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                        <Button
                          type="button"
                          className="bg-gradient-to-r from-[#d4af37] to-[#b08d26] text-[#1e3a5f] font-semibold rounded-lg"
                          disabled={uploadingImage}
                          onClick={() => document.querySelector('input[type="file"]').click()}
                          data-testid="upload-home-image-btn"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingImage ? "Uploading..." : "Upload Image"}
                        </Button>
                      </label>
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Image Guidelines</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Recommended resolution: 1920x1080 pixels (Full HD)</li>
                    <li>• Maximum file size: 5MB</li>
                    <li>• Supported formats: JPG, PNG, WebP</li>
                    <li>• The image will have a semi-transparent overlay to ensure text readability</li>
                    <li>• Please use copyright-free or original images only</li>
                  </ul>
                </div>

                {/* Divider */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">Forgot Password - Contact Details</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Configure the admin contact details shown on the "Forgot Password" page.
                  </p>
                </div>

                {/* Contact Settings Form */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Phone Number */}
                    <div>
                      <Label htmlFor="admin_phone" className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-[#1e3a5f]" />
                        Admin Phone Number
                      </Label>
                      <Input
                        id="admin_phone"
                        value={contactSettings.admin_phone}
                        onChange={(e) => setContactSettings({ ...contactSettings, admin_phone: e.target.value })}
                        className="mt-1 h-11 rounded-lg"
                        placeholder="+91 98765 43210"
                        data-testid="admin-phone-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Display format with spaces (e.g., +91 96991 36479)</p>
                    </div>

                    {/* WhatsApp Number */}
                    <div>
                      <Label htmlFor="admin_whatsapp" className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4 text-green-600" />
                        WhatsApp Number
                      </Label>
                      <Input
                        id="admin_whatsapp"
                        value={contactSettings.admin_whatsapp}
                        onChange={(e) => setContactSettings({ ...contactSettings, admin_whatsapp: e.target.value })}
                        className="mt-1 h-11 rounded-lg"
                        placeholder="919876543210"
                        data-testid="admin-whatsapp-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Numbers only, with country code (e.g., 919699136479)</p>
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="admin_email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-blue-600" />
                        Admin Email
                      </Label>
                      <Input
                        id="admin_email"
                        type="email"
                        value={contactSettings.admin_email}
                        onChange={(e) => setContactSettings({ ...contactSettings, admin_email: e.target.value })}
                        className="mt-1 h-11 rounded-lg"
                        placeholder="admin@example.com"
                        data-testid="admin-email-input"
                      />
                    </div>

                    {/* Support Hours */}
                    <div>
                      <Label htmlFor="support_hours" className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-600" />
                        Support Hours
                      </Label>
                      <Input
                        id="support_hours"
                        value={contactSettings.support_hours}
                        onChange={(e) => setContactSettings({ ...contactSettings, support_hours: e.target.value })}
                        className="mt-1 h-11 rounded-lg"
                        placeholder="Mon-Sat, 9:00 AM - 6:00 PM IST"
                        data-testid="support-hours-input"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t border-slate-200">
                    <Button
                      onClick={handleSaveContactSettings}
                      disabled={savingContact}
                      className="bg-gradient-to-r from-[#1e3a5f] to-[#162c46] text-white font-semibold rounded-lg"
                      data-testid="save-contact-btn"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {savingContact ? "Saving..." : "Save Contact Settings"}
                    </Button>
                  </div>
                </div>

                {/* Preview Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 mb-2">Preview - How users will see it</h4>
                  <div className="flex flex-wrap gap-4 text-sm text-amber-700">
                    <span className="flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      {contactSettings.admin_phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp: {contactSettings.admin_whatsapp}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {contactSettings.admin_email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {contactSettings.support_hours}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Item Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader style={{ background: 'linear-gradient(to right, #0f2744, #1e3a5f)', color: 'white' }}>
              <DialogTitle style={{ color: 'white' }}>Add New Item</DialogTitle>
              <DialogDescription style={{ color: '#cbd5e1' }}>Add a new {addDialogType.replace(/_/g, " ")} to the system.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 p-6 bg-white">
              {(addDialogType === "amenities" || addDialogType === "property_features") ? (
                <div>
                  <Label htmlFor="label">Name</Label>
                  <Input
                    id="label"
                    value={newItem.label}
                    onChange={(e) => setNewItem({ ...newItem, label: e.target.value, value: e.target.value })}
                    className="mt-1 h-11 rounded-lg"
                    placeholder="Enter name"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="label">Display Name</Label>
                    <Input
                      id="label"
                      value={newItem.label}
                      onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                      className="mt-1 h-11 rounded-lg"
                      placeholder="e.g., Premium Villa"
                    />
                  </div>
                  <div>
                    <Label htmlFor="value">System Code</Label>
                    <Input
                      id="value"
                      value={newItem.value}
                      onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                      className="mt-1 h-11 rounded-lg"
                      placeholder="e.g., premium_villa"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Use lowercase with underscores, no spaces
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDialog(false)}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddItem}
                className="bg-[#1e3a5f] text-white rounded-lg"
              >
                Add Item
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
